// 뷰어가 만든 파일을 사용자 PC 에 내려받는 **한 곳**(세션 녹화 §9.7 · 채팅 대화 저장 §9.6).
//
// 🔴 **왜 한 곳에 모으나**(2026-09-23 앱 실기에서 값비싸게 확인): 통합앱의 WebView2 에서는
//    `<a download>` 가 **파일도 만들지 않고 오류도 내지 않는다** — 조용히 사라진다.
//    브라우저에서만 되는 그 경로를 앱에서도 된다고 믿고 채팅 [대화 저장]을 그렇게 만들어 뒀고,
//    그래서 **앱에서는 저장이 한 번도 되지 않았다**(오류가 없으니 아무도 몰랐다).
//    녹화는 세션이 끝나면 다시 만들 수 없으므로 같은 실수를 반복하면 녹화를 통째로 잃는다.
//
//  ⇒ 앱이면 네이티브 명령(`save_download`)으로 내려받기 폴더에 쓰고, 브라우저면 `<a download>` 를 쓴다.

import { IS_SHELL } from './shell';

/** 저장 결과 — 앱은 저장된 경로를 돌려준다(브라우저는 알 수 없다). */
export interface SaveResult {
  ok: boolean;
  /** 앱에서 저장된 전체 경로(브라우저는 undefined). */
  path?: string;
  /** 실패 사유(사용자에게 보일 문구). */
  error?: string;
}

/**
 * 파일 한 개를 사용자 PC 에 저장한다.
 *
 * @param name 파일 이름(경로가 아니다 — 앱 쪽에서 구분자·`..` 를 막는다)
 * @param data 내용
 * @param mime 브라우저 경로에서 쓸 MIME
 */
export async function saveFileToDisk(
  name: string,
  data: BlobPart,
  mime: string,
): Promise<SaveResult> {
  if (IS_SHELL) {
    try {
      const bytes = new Uint8Array(await new Blob([data]).arrayBuffer());
      const { invoke } = await import('@tauri-apps/api/core');
      // 본문은 **바이너리**로 보낸다 — 녹화 영상은 수백 MB 가 될 수 있어 JSON 배열로 부풀릴 수 없다.
      const path = await invoke<string>('save_download', bytes, {
        // 한글 이름은 그대로 머리에 실리지 않는다 → 감싸 보내고 네이티브가 되돌린다.
        headers: { 'x-rs-name': encodeURIComponent(name) },
      });
      return { ok: true, path };
    } catch (e) {
      return { ok: false, error: `파일을 저장하지 못했습니다: ${String(e)}` };
    }
  }
  try {
    const url = URL.createObjectURL(new Blob([data], { type: mime }));
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    // 클릭이 처리된 뒤에 놓는다(즉시 놓으면 빈 파일로 저장되는 브라우저가 있다).
    window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: `파일을 저장하지 못했습니다: ${String(e)}` };
  }
}

/**
 * 저장 뒤 사람에게 보일 안내(대화 저장 · 녹화 저장이 같이 쓴다).
 *
 * 🔴 2026-09-24 Mac 실기: [대화 저장]을 누르면 파일은 **실제로 생겼는데** 화면에 아무 표시가 없어
 *    사용자가 "반응 없음" 으로 보고했다(같은 파일이 (3)·(4) 로 쌓여 있었다). 녹화도 "어디 저장됐는지 모른다".
 *    ⇒ 성공도 반드시 알린다 — 경로와 [폴더 열기]까지.
 */
export interface SaveNotice {
  kind: 'ok' | 'error';
  text: string;
  /** 앱에서 저장된 경로 — 있으면 [폴더 열기]를 보인다. */
  path?: string;
}

/**
 * 저장 결과를 안내로 바꾼다.
 * @param what 무엇을 저장했는지(문구 앞머리) — 예: `대화를`, `녹화를`
 */
export function saveNoticeOf(what: string, r: SaveResult): SaveNotice {
  if (!r.ok) {
    return { kind: 'error', text: r.error ?? '파일을 저장하지 못했습니다.' };
  }
  if (r.path) {
    return { kind: 'ok', text: `${what} 저장했습니다: ${r.path}`, path: r.path };
  }
  // 브라우저는 경로를 알 수 없다 — 브라우저의 다운로드 목록에 뜬다.
  return { kind: 'ok', text: `${what} 내려받았습니다(브라우저 다운로드 폴더).` };
}

/** Tauri 명령 이름 — 🔴 src-tauri `files::reveal_download` 와 같아야 한다. */
const REVEAL_COMMAND = 'reveal_download';

/**
 * 저장한 파일을 탐색기·Finder 에서 선택된 채로 연다(앱 전용). 브라우저에서는 아무것도 하지 않는다.
 * 네이티브가 **내려받기 폴더의 파일만** 열어 준다(웹이 임의 경로를 열게 하지 않는다).
 */
export async function revealSavedFile(path: string): Promise<void> {
  if (!IS_SHELL) {
    return;
  }
  const { invoke } = await import('@tauri-apps/api/core');
  await invoke(REVEAL_COMMAND, { path });
}
